import styled from '@emotion/styled';
import { MaxMobileWindowWidth } from '@racemap/utilities/consts/common';
import { EventTypes } from '@racemap/utilities/consts/events';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import { isActivated } from '@racemap/utilities/functions/event';
import {
  eventIsNeitherFreeNorPaid,
  isAtomicEvent,
  isChildEvent,
  isGroupEvent,
} from '@racemap/utilities/functions/utils';
import { Button, Space } from 'antd';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useIsSafemap } from 'src/lib/customHooks/useIsSafemap';
import { getLeaderboardUrl } from 'src/lib/getLeaderboardUrl';
import { getPlayerUrl } from 'src/lib/getPlayerUrl';
import { getSafemapUrl } from '../../../lib/EventUtils';
import type { CurrentEvent } from '../../../store/events/events_reducers';
import { ButtonWithCopy } from '../../BasicComponents/ButtonWithCopy';
import { PopoverHint } from '../../BasicComponents/PopoverHint';
import { IconLeaderboard, IconMap, IconSafemap } from '../../Icon';
import { PayButton } from './PayButton';

interface Props {
  event: Immutable<CurrentEvent>;
}

export const Buttons: FC<Props> = ({ event }) => {
  const user = useCurrentUser();
  const isSafemap = useIsSafemap();

  if (user == null) return <></>;
  const {
    modules: { timing },
  } = event;

  return (
    <Container>
      {isChildEvent(event) && <GroupReturnButton event={event} />}
      {!isSafemap && <PlayerButton event={event} />}
      {isSafemap && <SafemapButton event={event} />}

      {isActivated(timing) && <LeaderboardButton event={event} />}

      {isAtomicEvent(event) && eventIsNeitherFreeNorPaid(event) && (
        <PayButton event={event} user={user} />
      )}
    </Container>
  );
};

export const PlayerButton: FC<{ event: Immutable<CurrentEvent> }> = ({ event }) => {
  const label = (
    <Space size={4}>
      {eventIsNeitherFreeNorPaid(event) ? 'Preview' : 'Tracking Map'}
      {isGroupEvent(event) && (
        <PopoverHint placement="bottom">
          On desktop applications, the group URL preselects specific single events, automatically.
          During the event, ALL RUNNING single events in the group are selected, automatically.
          After the event, the 1st single event in group settings is selected, automatically.
        </PopoverHint>
      )}
    </Space>
  );
  return (
    <ButtonWithCopy
      link={getPlayerUrl(event)}
      icon={IconMap}
      label={label}
      className={TestSelectors.admin.eventEditor.titleBar.previewButton.toString()}
    />
  );
};

export const SafemapButton: FC<{ event: Immutable<CurrentEvent> }> = ({ event }) => {
  const label = <Space size={4}>{eventIsNeitherFreeNorPaid(event) ? 'Preview' : 'Safemap'}</Space>;
  return (
    <ButtonWithCopy
      link={getSafemapUrl(event)}
      icon={IconSafemap}
      label={label}
      className={TestSelectors.admin.eventEditor.titleBar.previewButton.toString()}
    />
  );
};

const LeaderboardButton: FC<{ event: Immutable<CurrentEvent> }> = ({ event }) => (
  <ButtonWithCopy icon={IconLeaderboard} link={getLeaderboardUrl(event)} label="Leaderboard" />
);

const GroupReturnButton: FC<{ event: Immutable<CurrentEvent> }> = ({ event }) => {
  const navigate = useNavigate();

  return (
    <Button
      variant="outlined"
      style={{ display: 'flex', alignItems: 'center' }}
      onClick={() => navigate(`/admin/events/${event.parent}`)}
    >
      Go to {event.type === EventTypes.STAGE ? ' stage ' : ' contest '} group
    </Button>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  @media (max-width: ${MaxMobileWindowWidth}px) {
    margin: 2px 0;
    .btn {
      height: 25px;
    }

    .btn-group {
      height: 25px;
    }

    .hint {
      display: none;
    }

    .copy-button-action-button {
      display: none;
    }

    .copy-button-link-button {
      border-top-right-radius: 0.25rem !important;
      border-bottom-right-radius: 0.25rem !important;
    }
  }
`;
